import {
  BarChart3,
  Brain,
  Cpu,
  Globe,
  Lock,
  type LucideIcon,
  Rocket,
  Server,
  Shield,
  Wallet,
  Zap,
} from "lucide-react";

export type EcosystemIconKey =
  | "server"
  | "zap"
  | "shield"
  | "barChart3"
  | "globe"
  | "brain"
  | "cpu"
  | "wallet"
  | "rocket"
  | "lock";

export type EcosystemCard = {
  id: string;
  icon: EcosystemIconKey;
  title: string;
  description: string;
  buttonLabel: string;
  buttonLink: string;
};

export const ECOSYSTEM_ICONS: Record<EcosystemIconKey, { label: string; Icon: LucideIcon }> = {
  server: { label: "Server", Icon: Server },
  zap: { label: "Lightning", Icon: Zap },
  shield: { label: "Shield", Icon: Shield },
  barChart3: { label: "Charts", Icon: BarChart3 },
  globe: { label: "Global", Icon: Globe },
  brain: { label: "AI", Icon: Brain },
  cpu: { label: "CPU", Icon: Cpu },
  wallet: { label: "Wallet", Icon: Wallet },
  rocket: { label: "Growth", Icon: Rocket },
  lock: { label: "Security", Icon: Lock },
};

export const DEFAULT_ECOSYSTEM_CARDS: EcosystemCard[] = [
  {
    id: "vps",
    icon: "server",
    title: "Ultra-Low Latency VPS",
    description: "Colocated servers in NY4 and LD4. Average latency to major brokers: 0.8ms.",
    buttonLabel: "Explore Servers",
    buttonLink: "/vps",
  },
  {
    id: "ea",
    icon: "zap",
    title: "Algorithmic Edge (EA)",
    description:
      "Curated list of trading robots with audited Myfxbook track records. From scalpers to trend-followers.",
    buttonLabel: "View Directory",
    buttonLink: "/eas",
  },
];

function parseJsonIfString(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const s = value.trim();
  if (!s) return value;
  const looksLikeJson = (s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"));
  if (!looksLikeJson) return value;
  try {
    return JSON.parse(s);
  } catch {
    return value;
  }
}

function ensureRecord(value: unknown): Record<string, any> {
  const v = parseJsonIfString(value);
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  return v as Record<string, any>;
}

export function normalizeEcosystemCards(input: any, opts?: { fallback?: boolean }): EcosystemCard[] {
  const record = ensureRecord(input);
  const raw = Array.isArray(record.items) ? record.items : Array.isArray(input) ? input : [];
  const items = Array.isArray(raw) ? raw : [];

  const normalized = items
    .map((it: any, idx: number) => {
      const title = String(it?.title || it?.name || "").trim();
      const description = String(it?.description || it?.subtitle || it?.desc || "").trim();
      const buttonLabel = String(it?.buttonLabel || it?.buttonName || it?.ctaLabel || "").trim();
      const buttonLink = String(it?.buttonLink || it?.link || it?.href || "").trim();
      const iconRaw = String(it?.icon || "").trim();
      const icon =
        iconRaw && iconRaw in ECOSYSTEM_ICONS
          ? (iconRaw as EcosystemIconKey)
          : DEFAULT_ECOSYSTEM_CARDS[idx % DEFAULT_ECOSYSTEM_CARDS.length]?.icon || "server";

      if (!title && !description && !buttonLabel && !buttonLink) return null;

      return {
        id: String(it?.id || it?.slug || title || idx),
        icon,
        title: title || `Card ${idx + 1}`,
        description,
        buttonLabel: buttonLabel || "Explore",
        buttonLink: buttonLink || "/",
      } satisfies EcosystemCard;
    })
    .filter(Boolean) as EcosystemCard[];

  if (normalized.length) return normalized;
  return opts?.fallback === false ? [] : DEFAULT_ECOSYSTEM_CARDS;
}
